/**
 * PlaylistsPage - Step 8: User playlists management
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ListMusic, Plus, Play, MoreVertical, Edit2, Trash2, 
  Lock, Globe, Music, ChevronLeft, X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlaylistCard = ({ playlist, onClick, onEdit, onDelete }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className="bg-[#12101A] rounded-2xl p-4 border border-white/5 cursor-pointer group"
    onClick={onClick}
  >
    <div className="relative aspect-square rounded-xl bg-gradient-to-br from-[#FF007F]/20 to-[#00F0FF]/20 mb-3 flex items-center justify-center overflow-hidden">
      {playlist.cover_image ? (
        <img src={playlist.cover_image} alt={playlist.name} className="w-full h-full object-cover" />
      ) : (
        <ListMusic className="w-12 h-12 text-[#71717A]" />
      )}
      
      {/* Play overlay */}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <div className="w-12 h-12 rounded-full bg-[#00F0FF] flex items-center justify-center">
          <Play className="w-6 h-6 text-[#050505] ml-1" />
        </div>
      </div>
      
      {/* Privacy indicator */}
      <div className="absolute top-2 right-2">
        {playlist.is_public ? (
          <Globe className="w-4 h-4 text-white/60" />
        ) : (
          <Lock className="w-4 h-4 text-white/60" />
        )}
      </div>
    </div>
    
    <h3 className="text-white font-semibold truncate">{playlist.name}</h3>
    <p className="text-[#71717A] text-sm">{playlist.track_count || 0} tracks</p>
    
    {/* Actions */}
    <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(playlist); }}
        className="flex-1 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20 transition-colors flex items-center justify-center gap-1"
      >
        <Edit2 className="w-3 h-3" />
        Edit
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(playlist.id); }}
        className="py-2 px-3 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  </motion.div>
);

const CreatePlaylistModal = ({ visible, onClose, onSubmit, editPlaylist }) => {
  const [name, setName] = useState(editPlaylist?.name || '');
  const [description, setDescription] = useState(editPlaylist?.description || '');
  const [isPublic, setIsPublic] = useState(editPlaylist?.is_public ?? true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editPlaylist) {
      setName(editPlaylist.name);
      setDescription(editPlaylist.description || '');
      setIsPublic(editPlaylist.is_public);
    } else {
      setName('');
      setDescription('');
      setIsPublic(true);
    }
  }, [editPlaylist, visible]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    
    setLoading(true);
    try {
      await onSubmit({ name: name.trim(), description: description.trim(), is_public: isPublic });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-full max-w-md bg-[#12101A] rounded-2xl p-6 border border-white/10"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 text-[#71717A] hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Unbounded' }}>
          {editPlaylist ? 'Edit Playlist' : 'Create Playlist'}
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-[#A1A1AA] text-sm mb-2">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Playlist"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF]"
              data-testid="playlist-name-input"
            />
          </div>
          
          <div>
            <label className="block text-[#A1A1AA] text-sm mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] resize-none"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              {isPublic ? <Globe className="w-5 h-5 text-[#00F0FF]" /> : <Lock className="w-5 h-5 text-[#FF007F]" />}
              <div>
                <p className="text-white font-medium">{isPublic ? 'Public' : 'Private'}</p>
                <p className="text-[#71717A] text-xs">{isPublic ? 'Anyone can see' : 'Only you can see'}</p>
              </div>
            </div>
            <button
              onClick={() => setIsPublic(!isPublic)}
              className={`w-12 h-6 rounded-full transition-colors ${isPublic ? 'bg-[#00F0FF]' : 'bg-white/20'}`}
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${isPublic ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
        
        <button
          onClick={handleSubmit}
          disabled={!name.trim() || loading}
          className="w-full mt-6 py-4 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="save-playlist-btn"
        >
          {loading ? 'Saving...' : editPlaylist ? 'Save Changes' : 'Create Playlist'}
        </button>
      </motion.div>
    </motion.div>
  );
};

const PlaylistsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editPlaylist, setEditPlaylist] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchPlaylists();
  }, [isAuthenticated, navigate]);

  const fetchPlaylists = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/artists/playlists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaylists(response.data.playlists || []);
    } catch (err) {
      console.error('Error fetching playlists:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (data) => {
    const token = localStorage.getItem('token');
    
    if (editPlaylist) {
      // Update
      await axios.put(
        `${API_URL}/api/artists/playlists/${editPlaylist.id}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } else {
      // Create
      await axios.post(
        `${API_URL}/api/artists/playlists`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }
    
    setEditPlaylist(null);
    fetchPlaylists();
  };

  const handleDelete = async (playlistId) => {
    if (!window.confirm('Delete this playlist?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/artists/playlists/${playlistId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPlaylists();
    } catch (err) {
      console.error('Error deleting playlist:', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
              My Playlists
            </h1>
          </div>
          
          <button
            onClick={() => { setEditPlaylist(null); setShowCreateModal(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-medium"
            data-testid="create-playlist-btn"
          >
            <Plus className="w-4 h-4" />
            New Playlist
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"
            />
          </div>
        ) : playlists.length === 0 ? (
          <div className="text-center py-16">
            <ListMusic className="w-16 h-16 mx-auto mb-4 text-[#52525B]" />
            <h3 className="text-white font-semibold text-lg mb-2">No playlists yet</h3>
            <p className="text-[#71717A] mb-6">Create your first playlist to save your favorite tracks</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 rounded-xl bg-[#00F0FF] text-[#050505] font-medium"
            >
              Create Playlist
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {playlists.map((playlist) => (
              <PlaylistCard
                key={playlist.id}
                playlist={playlist}
                onClick={() => navigate(`/playlist/${playlist.id}`)}
                onEdit={(p) => { setEditPlaylist(p); setShowCreateModal(true); }}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </main>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreatePlaylistModal
            visible={showCreateModal}
            onClose={() => { setShowCreateModal(false); setEditPlaylist(null); }}
            onSubmit={handleCreateOrUpdate}
            editPlaylist={editPlaylist}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlaylistsPage;
